import React from "react";
import PropTypes from "prop-types";
import {
    Box,
    Autocomplete,
    TextField,
    Checkbox,
    Chip,
    ListItemText,
    MenuItem,
} from "@mui/material";
import { FormField } from "../../../components/common/FormField";
import { ActionButtons } from "../../../components/common/ActionButton";
import { useForm, Controller } from "react-hook-form";
import { useNotification } from "../../../context/NotificationContext";
import {
    useCreateTodoItemMutation,
    useGetUserListQuery,
} from "../../../redux/service/modules/sales";
import CommonDialog from "../../../components/common/CommonDialog";
import { useResponsiveStyles } from "../../../styles/useResponsiveStyles";

const TodoItemsDialog = ({ open, onClose, title, ctaText }) => {
    const { control, handleSubmit, reset, setError } = useForm();
    const { getFontSize } = useResponsiveStyles();
    const [createTodoItem, { isLoading: isCreating }] =
        useCreateTodoItemMutation();
    const { data: userList } = useGetUserListQuery();
    const { showNotification } = useNotification();

    const handleClose = () => {
        reset();
        onClose();
    };

    const onSubmit = async (data) => {
        try {
            const formDataToSend = {};
            Object.keys(data).forEach((key) => {
                formDataToSend[key] = data[key];
            });
            await createTodoItem({ data: formDataToSend })
                .unwrap()
                .then((response) => {
                    if (response) {
                        showNotification(
                            "success",
                            "",
                            "To-Do Item Created successfully!"
                        );
                        handleClose();
                    } else {
                        showNotification(
                            "error",
                            "An Error Occurred",
                            "Failed to Create To-Do Item"
                        );
                        handleClose();
                    }
                })
                .catch((error) => {
                    if (error.status === 400) {
                        setError("trader_contact", {
                            type: "manual",
                            message: "Failed to create To-Do Item",
                        });
                    }
                });
        } catch (error) {
            console.error("Failed to update trader:", error);
        }
    };

    return (
        <CommonDialog open={open} onClose={handleClose} title={title}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
                <Controller
                    name="title"
                    control={control}
                    rules={{
                        required: "Title is required",
                    }}
                    render={({ field, fieldState }) => (
                        <FormField
                            label="Title"
                            {...field}
                            required
                            error={!!fieldState.error}
                            helperText={
                                fieldState.error ? fieldState.error.message : ""
                            }
                        />
                    )}
                />

                <div className="space-y-2">
                    <label className="block xl:text-xs 2xl:text-sm 3xl:text-base font-medium text-gray-700">
                        Recipients<span className="text-red-500"> *</span>
                    </label>
                    <Controller
                        name="recipients"
                        control={control}
                        rules={{
                            required: "Recipients are required",
                        }}
                        render={({ field, fieldState }) => (
                            <div className="relative">
                                <Autocomplete
                                    multiple
                                    disableCloseOnSelect
                                    options={userList || []}
                                    getOptionLabel={(option) => option.username}
                                    value={
                                        (userList || []).filter((option) =>
                                            field.value?.includes(option.id)
                                        ) || []
                                    }
                                    onChange={(_, newValue) => {
                                        field.onChange(
                                            newValue.map((item) => item.id)
                                        );
                                    }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            variant="outlined"
                                            className="w-full xl:text-xs 2xl:text-sm 3xl:text-base"
                                            sx={{
                                                "& .MuiOutlinedInput-root": {
                                                    padding:
                                                        "0px 8px !important",
                                                    "& .MuiOutlinedInput-notchedOutline":
                                                        {
                                                            border: "none",
                                                        },
                                                },
                                                "& .MuiInputBase-root": {
                                                    border: "1px solid rgb(229, 231, 235)",
                                                    borderRadius: "0.375rem",
                                                    "@media (min-width: 1280px)":
                                                        {
                                                            borderWidth: "1px",
                                                            borderRadius:
                                                                "0.375rem",
                                                        },
                                                    "@media (min-width: 1536px)":
                                                        {
                                                            borderWidth: "2px",
                                                            borderRadius:
                                                                "0.5rem",
                                                        },
                                                    "@media (min-width: 1920px)":
                                                        {
                                                            borderWidth: "2px",
                                                            borderRadius:
                                                                "0.5rem",
                                                        },
                                                    "&:hover": {
                                                        borderColor:
                                                            "rgb(239, 68, 68)",
                                                    },
                                                    "&.Mui-focused": {
                                                        borderColor:
                                                            "rgb(239, 68, 68)",
                                                        boxShadow:
                                                            "0 0 0 2px rgba(239, 68, 68, 0.2)",
                                                    },
                                                },
                                                "& .MuiAutocomplete-input": {
                                                    padding:
                                                        "0.375rem 0.5rem !important",
                                                    "@media (min-width: 1280px)":
                                                        {
                                                            fontSize: "0.75rem",
                                                            padding:
                                                                "0.375rem 0.5rem !important",
                                                        },
                                                    "@media (min-width: 1536px)":
                                                        {
                                                            fontSize:
                                                                "0.875rem",
                                                            padding:
                                                                "0.5rem 0.75rem !important",
                                                        },
                                                    "@media (min-width: 1920px)":
                                                        {
                                                            fontSize: "1rem",
                                                            padding:
                                                                "0.625rem 1rem !important",
                                                        },
                                                },
                                            }}
                                        />
                                    )}
                                    renderOption={(
                                        props,
                                        option,
                                        { selected }
                                    ) => (
                                        <MenuItem
                                            {...props}
                                            className="xl:text-xs 2xl:text-sm 3xl:text-base"
                                            sx={{
                                                transition:
                                                    "background-color 0.1s ease",
                                                padding: "2px 8px",
                                                minHeight: "32px",
                                            }}
                                        >
                                            <Checkbox
                                                checked={selected}
                                                className="xl:p-0.5 2xl:p-1 3xl:p-1.5"
                                                sx={{
                                                    color: "rgb(239, 68, 68)",
                                                    padding: "4px",
                                                    "&.Mui-checked": {
                                                        color: "rgb(239, 68, 68)",
                                                    },
                                                    "& .MuiSvgIcon-root": {
                                                        transition:
                                                            "transform 0.1s ease",
                                                        fontSize: "18px",
                                                    },
                                                }}
                                            />
                                            <ListItemText
                                                primary={option.username}
                                                className="xl:ml-1 2xl:ml-2 3xl:ml-3"
                                                primaryTypographyProps={{
                                                    className:
                                                        "xl:text-xs 2xl:text-sm 3xl:text-base",
                                                }}
                                            />
                                        </MenuItem>
                                    )}
                                    renderTags={(tagValue, getTagProps) =>
                                        tagValue.map((option, index) => (
                                            <Chip
                                                {...getTagProps({ index })}
                                                key={option.id}
                                                label={option.username}
                                                className="xl:text-xs 2xl:text-sm 3xl:text-base"
                                                sx={{
                                                    backgroundColor:
                                                        "rgb(254, 226, 226)",
                                                    color: "black",
                                                    height: "24px",
                                                    margin: "1px !important",
                                                    "& .MuiChip-label": {
                                                        padding: "0 6px",
                                                    },
                                                    "& .MuiChip-deleteIcon": {
                                                        color: "rgb(239, 68, 68)",
                                                        "&:hover": {
                                                            color: "rgb(220, 38, 38)",
                                                        },
                                                    },
                                                }}
                                            />
                                        ))
                                    }
                                />
                                {fieldState.error && (
                                    <span className="block xl:text-xs 2xl:text-sm 3xl:text-base text-red-500 mt-1">
                                        {fieldState.error.message}
                                    </span>
                                )}
                            </div>
                        )}
                    />
                </div>

                <Controller
                    name="deadline"
                    control={control}
                    rules={{
                        required: "Deadline is required",
                    }}
                    render={({ field, fieldState }) => {
                        return (
                            <FormField
                                label="Deadline"
                                type="datetime-local"
                                {...field}
                                required
                                error={!!fieldState.error}
                                helperText={
                                    fieldState.error
                                        ? fieldState.error.message
                                        : ""
                                }
                                InputLabelProps={{
                                    shrink: true, // Ensures the label works well with datetime-local
                                }}
                                min={new Date().toISOString().slice(0, 16)}
                            />
                        );
                    }}
                />

                <div className="space-y-2">
                    <label className="block xl:text-xs 2xl:text-sm 3xl:text-base font-medium text-gray-700">
                        Type of Action<span className="text-red-500"> *</span>
                    </label>
                    <Controller
                        name="type_of_action"
                        control={control}
                        rules={{
                            required: "Type of Action is required",
                        }}
                        render={({ field, fieldState }) => (
                            <div className="relative">
                                <Autocomplete
                                    multiple
                                    disableCloseOnSelect
                                    options={["phone", "email", "in-person"]}
                                    getOptionLabel={(option) => option}
                                    value={
                                        ["phone", "email", "in-person"].filter(
                                            (option) =>
                                                field.value?.includes(option)
                                        ) || []
                                    }
                                    onChange={(_, newValue) => {
                                        field.onChange(
                                            newValue.map((item) => item)
                                        );
                                    }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            variant="outlined"
                                            className="w-full xl:text-xs 2xl:text-sm 3xl:text-base"
                                            sx={{
                                                "& .MuiOutlinedInput-root": {
                                                    padding:
                                                        "0px 8px !important",
                                                    "& .MuiOutlinedInput-notchedOutline":
                                                        {
                                                            border: "none",
                                                        },
                                                },
                                                "& .MuiInputBase-root": {
                                                    border: "1px solid rgb(229, 231, 235)",
                                                    borderRadius: "0.375rem",
                                                    "@media (min-width: 1280px)":
                                                        {
                                                            borderWidth: "1px",
                                                            borderRadius:
                                                                "0.375rem",
                                                        },
                                                    "@media (min-width: 1536px)":
                                                        {
                                                            borderWidth: "2px",
                                                            borderRadius:
                                                                "0.5rem",
                                                        },
                                                    "@media (min-width: 1920px)":
                                                        {
                                                            borderWidth: "2px",
                                                            borderRadius:
                                                                "0.5rem",
                                                        },
                                                    "&:hover": {
                                                        borderColor:
                                                            "rgb(239, 68, 68)",
                                                    },
                                                    "&.Mui-focused": {
                                                        borderColor:
                                                            "rgb(239, 68, 68)",
                                                        boxShadow:
                                                            "0 0 0 2px rgba(239, 68, 68, 0.2)",
                                                    },
                                                },
                                                "& .MuiAutocomplete-input": {
                                                    padding:
                                                        "0.375rem 0.5rem !important",
                                                    "@media (min-width: 1280px)":
                                                        {
                                                            fontSize: "0.75rem",
                                                            padding:
                                                                "0.375rem 0.5rem !important",
                                                        },
                                                    "@media (min-width: 1536px)":
                                                        {
                                                            fontSize:
                                                                "0.875rem",
                                                            padding:
                                                                "0.5rem 0.75rem !important",
                                                        },
                                                    "@media (min-width: 1920px)":
                                                        {
                                                            fontSize: "1rem",
                                                            padding:
                                                                "0.625rem 1rem !important",
                                                        },
                                                },
                                            }}
                                        />
                                    )}
                                    renderOption={(
                                        props,
                                        option,
                                        { selected }
                                    ) => (
                                        <MenuItem
                                            {...props}
                                            className="xl:text-xs 2xl:text-sm 3xl:text-base"
                                            sx={{
                                                transition:
                                                    "background-color 0.1s ease",
                                                padding: "2px 8px",
                                                minHeight: "32px",
                                            }}
                                        >
                                            <Checkbox
                                                checked={selected}
                                                className="xl:p-0.5 2xl:p-1 3xl:p-1.5"
                                                sx={{
                                                    color: "rgb(239, 68, 68)",
                                                    padding: "4px",
                                                    "&.Mui-checked": {
                                                        color: "rgb(239, 68, 68)",
                                                    },
                                                    "& .MuiSvgIcon-root": {
                                                        transition:
                                                            "transform 0.1s ease",
                                                        fontSize: "18px",
                                                    },
                                                }}
                                            />
                                            <ListItemText
                                                primary={option}
                                                className="xl:ml-1 2xl:ml-2 3xl:ml-3"
                                                primaryTypographyProps={{
                                                    className:
                                                        "xl:text-xs 2xl:text-sm 3xl:text-base",
                                                }}
                                            />
                                        </MenuItem>
                                    )}
                                    renderTags={(tagValue, getTagProps) =>
                                        tagValue.map((option, index) => (
                                            <Chip
                                                {...getTagProps({ index })}
                                                key={option}
                                                label={option}
                                                className="xl:text-xs 2xl:text-sm 3xl:text-base"
                                                sx={{
                                                    backgroundColor:
                                                        "rgb(254, 226, 226)",
                                                    color: "black",
                                                    height: "24px",
                                                    margin: "2px !important",
                                                    "& .MuiChip-label": {
                                                        padding: "0 6px",
                                                    },
                                                    "& .MuiChip-deleteIcon": {
                                                        color: "rgb(239, 68, 68)",
                                                        "&:hover": {
                                                            color: "rgb(220, 38, 38)",
                                                        },
                                                    },
                                                }}
                                            />
                                        ))
                                    }
                                />
                                {fieldState.error && (
                                    <span className="block xl:text-xs 2xl:text-sm 3xl:text-base text-red-500 mt-1">
                                        {fieldState.error.message}
                                    </span>
                                )}
                            </div>
                        )}
                    />
                </div>

                <Controller
                    name="description"
                    control={control}
                    rules={{
                        required: "Description is required",
                    }}
                    render={({ field, fieldState }) => (
                        <FormField
                            label="Description"
                            type="text"
                            multiline
                            rows={4}
                            {...field}
                            required
                            error={!!fieldState.error}
                            helperText={
                                fieldState.error ? fieldState.error.message : ""
                            }
                        />
                    )}
                />
                <ActionButtons
                    onCancel={handleClose}
                    ctaText={ctaText}
                    onSave={handleSubmit(onSubmit)}
                    isLoading={isCreating}
                />
            </Box>
        </CommonDialog>
    );
};

TodoItemsDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    rowData: PropTypes.object,
};

export default TodoItemsDialog;
